/* Mes devoirs — élève (self-service, compte eleve connecté directement).
   Liste seule pour ce lot : rendu/PDF/correction restent le chemin parent
   (/tutor/enfant/$studentId), qui repose sur un studentId + lien vérifié —
   un compte élève n'a pas ce lien à lui-même. Étendre ce chemin à l'élève
   direct est un lot à part. */

import { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { api } from '../lib/api';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tray, WarningCircle, ArrowClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { groupAssignmentsByClassroomAndSubject } from '@naia-app/shared';
import { assignmentFormatLabel, AssignmentFormatIcon } from '../lib/assignment-format';

interface MyAssignment {
  assignment_id: string;
  title: string;
  format: 'closed' | 'interactive';
  due_at: string | null;
  submission_status: string;
  classroom_id: string;
  classroom_name: string;
  subject_id: string;
  subject_label: string;
  subject_position: number;
}

const STATUS_LABEL: Record<string, string> = {
  not_started: 'À faire',
  draft: 'En cours',
  submitted: 'Rendu',
};

function dueTimestamp(a: MyAssignment): number {
  return a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
}

function useGroupedMyAssignments(items: MyAssignment[]) {
  return useMemo(() => {
    const classroomNames = new Map<string, string>();
    const subjectLabels = new Map<string, string>();
    const subjectPositions = new Map<string, number>();
    for (const a of items) {
      classroomNames.set(a.classroom_id, a.classroom_name);
      subjectLabels.set(a.subject_id, a.subject_label);
      subjectPositions.set(a.subject_id, a.subject_position);
    }

    const groups = groupAssignmentsByClassroomAndSubject(items, {
      classroomIds: (a) => [a.classroom_id],
      subjectId: (a) => a.subject_id,
      subjectSortKey: (subjectId) => subjectPositions.get(subjectId) ?? Number.POSITIVE_INFINITY,
      dueTimestamp,
    });

    const sorted = [...groups].sort((a, b) =>
      (classroomNames.get(a.classroomId) ?? '').localeCompare(classroomNames.get(b.classroomId) ?? '', 'fr'),
    );

    return { groups: sorted, classroomNames, subjectLabels };
  }, [items]);
}

function AssignmentRow({ a }: { a: MyAssignment }) {
  return (
    <div className="flex items-center gap-3 w-full px-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-semibold text-[14px] text-foreground truncate">{a.title}</span>
          <Badge variant="outline" className="shrink-0 hidden sm:inline-flex gap-1 text-muted-foreground">
            <AssignmentFormatIcon format={a.format} />
            {assignmentFormatLabel(a.format)}
          </Badge>
        </div>
        <div className="flex items-center gap-3 text-[12px] flex-wrap">
          <span className="text-muted-foreground">
            {a.due_at
              ? `À rendre le ${new Date(a.due_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`
              : 'Sans date limite'}
          </span>
        </div>
      </div>
      <Badge>{STATUS_LABEL[a.submission_status] ?? a.submission_status}</Badge>
    </div>
  );
}

function AssignmentList({ items }: { items: readonly MyAssignment[] }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-col rounded-card border border-border overflow-hidden shadow-card">
      {items.map((a, i) => (
        <div key={a.assignment_id}>
          {i > 0 && <Separator />}
          <AssignmentRow a={a} />
        </div>
      ))}
    </div>
  );
}

function GroupedAssignments({ items }: { items: MyAssignment[] }) {
  const { groups, classroomNames, subjectLabels } = useGroupedMyAssignments(items);
  if (!items.length) return null;

  return (
    <div className="flex flex-col gap-6">
      {groups.map((g) => (
        <div key={g.classroomId}>
          <h2 className="text-[13px] font-semibold text-foreground/80 mb-2 px-0.5">
            {classroomNames.get(g.classroomId) ?? 'Classe'}
          </h2>
          <div className="flex flex-col gap-4">
            {g.subjectGroups.map((sg) => (
              <div key={sg.subjectId}>
                {g.subjectGroups.length > 1 && (
                  <h3 className="text-[12px] font-medium text-muted-foreground mb-1.5 px-0.5">
                    {subjectLabels.get(sg.subjectId) ?? ''}
                  </h3>
                )}
                <AssignmentList items={sg.items} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LoadFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-muted-foreground text-center px-4">
      <WarningCircle size={36} weight="light" className="mb-3 text-destructive opacity-80" />
      <p className="font-semibold text-[14px] text-foreground m-0">Tes devoirs n'ont pas pu être chargés</p>
      <Button onClick={onRetry} className="mt-4">
        <ArrowClockwise size={16} weight="bold" />
        Réessayer
      </Button>
    </div>
  );
}

export default function StudentAssignments() {
  const [rows, setRows] = useState<MyAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    api.student.assignments
      .query()
      .then((d) => setRows(d as MyAssignment[]))
      .catch(() => {
        setRows([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <Header />
      <HeaderContent width="md" showBurger>
        <h1 className={HEADER_TITLE_CLASS}>Mes devoirs</h1>
      </HeaderContent>
      <div className="max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full">
        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-[58px] rounded-card" />)}
          </div>
        ) : failed ? (
          <LoadFailed onRetry={load} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Tray} title="Aucun devoir pour l'instant" description="Les devoirs donnés par tes enseignants apparaîtront ici." />
        ) : (
          <GroupedAssignments items={rows} />
        )}
      </div>
    </div>
  );
}
